import React, { useCallback, useEffect, useRef, useState } from "react";
import { 
  Route, 
  Routes 
} from "react-router-dom";

import { CORE_DATA_TYPES, DATA_TYPE_INFO } from "@dndModels/Constants";
import { DataType } from "@dndModels/Types";
import { useAuth } from "react-oidc-context";

import UserIndex from "@views/user";
import UserEdit from "@views/user/edit";

// import DandDMainPage from "../systems/d&d/views/MainPage";
// import PathfinderMainPage from "../systems/pathfinder/views/MainPage";
// import MyTTRPGMainPage from "../systems/myttrpg/views/MainPage";

import Attributions from "@views/attributions";
import OGL from "@views/ogl";
import PrivacyPolicy from "@views/PrivacyPolicy";
import DataDeletion from "@views/DataDeletion";

import Login from "@components/Login";
import DandDAPI from "@dndUtilities/system_api";
// import UserAPI from "../utilities/user_api";

import LoadingBox from "@components/input/LoadingBox";

import AdminOnly from "@dndViews/AdminOnly";

import HomePage from "@views/Home";

import SpellParseAdd from "@dndViews/spell/parseAdd";

import CampaignIndex from "@dndViews/campaign";
import CampaignGMScreen from "@dndViews/campaign/gm_screen";
import CampaignDetails from "@dndViews/campaign/details";
import CampaignJoin from "@dndViews/campaign/join";
import CampaignDecks from "@dndViews/campaign/decks";

import CharacterIndex from "@dndViews/character";
import CharacterDetails from "@dndViews/character/details";
import CharacterShared from "@dndViews/character/shared";
import AllCharacters from "@dndViews/character/all";
import CharacterPrintable from "@dndViews/character/printable";

import EncounterIndex from "@dndViews/encounter";
import EncounterDetails from "@dndViews/encounter/details";

import CreatureMergeAdd from "@dndViews/creature/mergeAdd";
import CreatureParseAdd from "@dndViews/creature/parseAdd";

import ItemParseAdd from "@dndViews/item/parseAdd";
import GiftParseAdd from "@dndViews/gift/parseAdd";

import SourceBookIndex from "@dndViews/source_book";
import SourceBookEdit from "@dndViews/source_book/edit";

import NewUserInput from "@components/input/NewUserInput";

import StandardIndex from "./StandardIndex";
import StandardEdit from "./StandardEdit";

const COGNITO_DOMAIN = "https://us-west-2h7ntbqeqs.auth.us-west-2.amazoncognito.com";
const CLIENT_ID = "1ltrrg50umnijv7h38aku4ufpr";
const isLocal = window.location.hostname === "localhost";
const LOGOUT_URI = isLocal
  ? "http://localhost:3000"
  : "https://hbgamelabs.com";
  
interface Props { 
  loginOpen: boolean;
  userStatus: "" | "New User" | "Updating User" | "Error";
  onNewUserComplete: () => void;
}

const data_type_map: { [data_type: string]: string } = {}; 
CORE_DATA_TYPES.forEach(o => {
  const dt_info = DATA_TYPE_INFO.find(o2 => o2.data_type === o);
  if (dt_info) {
    data_type_map[o] = dt_info.plural;
  }
}); 

const api = DandDAPI.getInstance();

export default function DandDMainPage({ 
  loginOpen,
  userStatus,
  onNewUserComplete
}: Props) {
  const auth = useAuth();
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading_message, setLoadingMessage] = useState("Loading");
  const [percentage, setPercentage] = useState(0);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    if (!fetched) {
      setFetched(true);
    }
    
    const onComplete = () => {
      setLoading(false);
      api.subscription_holder.postReload();
    };
    const onProgress = (data_type: string, percent: number ) => {
      setPercentage(percent * 100);
      setLoadingMessage(`Loading ${data_type_map[data_type]}`);
    };
    const onError = (message: string) => {
      setLoadingMessage(message);
    };
    // console.log(data_type_map);
    api.getSetOfObjectsWithCallbacks({ data_types: Object.keys(data_type_map) as DataType[], 
      onComplete, onProgress, onError 
    });
  }, [fetched]);

  const reload = useCallback(() => {
    console.log("reload");
    api.smart_hash = {};
    api.temp_hash = {};
    clearInterval(api.temp_hash_interval);
    api.temp_hash_interval = null;
    load();
  }, [load]);

  // ── Sign Out ──────────────────────────────────────────────────────────────
  const handleSignOut = useCallback(() => {
    if (auth.isAuthenticated) {
      localStorage.setItem("redirectRememberURL", window.location.href);
      auth.removeUser();
      window.location.href = `${COGNITO_DOMAIN}/logout?client_id=${CLIENT_ID}&logout_uri=${encodeURIComponent(LOGOUT_URI)}`;
    } else {
      api.logout();
    }
  }, [auth]);

  useEffect(() => {
    api.subscription_holder.subscribe("Reload", mounted, reload);
    api.subscription_holder.subscribe("Logout", mounted, handleSignOut);
  }, [reload, handleSignOut]);

  if (!fetched || loading) {
    return (<LoadingBox message={loading_message} percentage={percentage} size={window.innerHeight * 0.5} height={window.innerHeight - 64} width={window.innerWidth - 24} />); // width={window.innerWidth - 17}
  } else if (userStatus !== "") {
    return (
      <NewUserInput 
        userStatus={userStatus} 
        onComplete={onNewUserComplete} 
      />
    );
  } else if (loginOpen) {
    return (
      <Login />
    );
  } else {
    console.log("Main Page");
    return (
      <Routes>
        <Route path="/" element={<HomePage/>} />
        <Route path="" element={<HomePage/>} />

        {/* Special Pages that don't fit the normal formula */}
        <Route path="/home" element={<HomePage/>} />
        <Route path="/attributions" element={<Attributions/>} />
        <Route path="/ogl" element={<OGL/>} />
        <Route path="/privacy" element={<PrivacyPolicy/>} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/admin_only" element={<AdminOnly/>} />
        <Route path="/spell/parse_add" element={<SpellParseAdd/>} />
        <Route path="/item/parse_add" element={<ItemParseAdd/>} />
        <Route path="/gift/parse_add" element={<GiftParseAdd/>} />
        {/* <Route path="/creature/from_character/:char_id" element={<CreatureEdit/>} /> */}
        <Route path="/creature/merge_add" element={<CreatureMergeAdd/>} />
        <Route path="/creature/parse_add" element={<CreatureParseAdd/>} />
        <Route path="/all_characters" element={<AllCharacters/>} />
        {/* <Route path="/source_book/create" element={<SourceBookEdit/>} />
        <Route path="/source_book/:id_or_abbrev" element={<SourceBookEdit/>} />
        <Route path="/source_book" element={<SourceBookIndex/>} /> */}
        
        {/* User Focused Pages */}
        <Route path="/campaign/decks/:id" element={<CampaignDecks/>} />
        <Route path="/campaign/join/:id/:invite" element={<CampaignJoin/>} />
        <Route path="/campaign/create" element={<CampaignGMScreen/>} />
        <Route path="/campaign/gm_screen/:id" element={<CampaignGMScreen/>} />
        <Route path="/campaign/:id" element={<CampaignDetails/>} />
        <Route path="/campaign" element={<CampaignIndex/>} />

        <Route path="/character/create_on_campaign/:campaign_id/:unassigned" element={<CharacterDetails/>} />
        <Route path="/character/shared/:id" element={<CharacterShared/>} />
        <Route path="/character/printable/:id" element={<CharacterPrintable/>} />
        <Route path="/character/:id" element={<CharacterDetails/>} />
        <Route path="/character" element={<CharacterIndex/>} />
        
        <Route path="/encounter/:id" element={<EncounterDetails/>} />
        <Route path="/encounter/create_on_campaign/:campaign_id/:unassigned" element={<EncounterDetails/>} />
        <Route path="/encounter" element={<EncounterIndex/>} />

        <Route path="/user" element={<UserIndex/>} />
        <Route path="/user/:id" element={<UserEdit/>} />

        <Route path="/:data_type/:sb_abbrev/:name" element={<StandardEdit />} />
        <Route path="/:data_type/:id_or_name" element={<StandardEdit />} />
        <Route path="/:data_type" element={<StandardIndex />} />

        {/* 

        <Route path="/skill" element={<SkillIndex/>} />
        <Route path="/skill/create" element={<SkillEdit/>} />
        <Route path="/skill/:id" element={<SkillEdit/>} />

        <Route path="/sense" element={<SenseIndex/>} />
        <Route path="/sense/create" element={<SenseEdit/>} />
        <Route path="/sense/:id" element={<SenseEdit/>} />

        <Route path="/deity" element={<DeityIndex/>} />
        <Route path="/deity/create" element={<DeityEdit/>} />
        <Route path="/deity/:id" element={<DeityEdit/>} />

        <Route path="/user_ticket" element={<UserTicketIndex/>} />
        <Route path="/user_ticket/create" element={<UserTicketEdit/>} />
        <Route path="/user_ticket/:id" element={<UserTicketEdit/>} />

        <Route path="/ticket" element={<TicketIndex/>} />
        <Route path="/ticket/create" element={<TicketEdit/>} />
        <Route path="/ticket/:id" element={<TicketEdit/>} />

        <Route path="/spell" element={<SpellIndex/>} />
        <Route path="/spell/create" element={<SpellEdit/>} />
        <Route path="/spell/:id" element={<SpellEdit/>} />

        <Route path="/spell_slot_type" element={<SpellSlotTableIndex/>} />
        <Route path="/spell_slot_type/create" element={<SpellSlotTableEdit/>} />
        <Route path="/spell_slot_type/:id" element={<SpellSlotTableEdit/>} />

        <Route path="/game_card" element={<GameCardIndex/>} />
        <Route path="/game_card/create" element={<GameCardEdit/>} />
        <Route path="/game_card/:id" element={<GameCardEdit/>} />

        <Route path="/game_deck" element={<GameDeckIndex/>} />
        <Route path="/game_deck/create" element={<GameDeckEdit/>} />
        <Route path="/game_deck/:id" element={<GameDeckEdit/>} />

        <Route path="/table" element={<TableIndex/>} />
        <Route path="/table/create" element={<TableEdit/>} />
        <Route path="/table/:id" element={<TableEdit/>} />

        <Route path="/armor_type" element={<ArmorTypeIndex/>} />
        <Route path="/armor_type/create" element={<ArmorTypeEdit/>} />
        <Route path="/armor_type/:id" element={<ArmorTypeEdit/>} />

        <Route path="/language" element={<LanguageIndex/>} />
        <Route path="/language/create" element={<LanguageEdit/>} />
        <Route path="/language/:id" element={<LanguageEdit/>} />

        <Route path="/reusable_string" element={<ReusableStringIndex/>} />
        <Route path="/reusable_string/create" element={<ReusableStringEdit/>} />
        <Route path="/reusable_string/:id" element={<ReusableStringEdit/>} />

        <Route path="/gift" element={<GiftIndex/>} />
        <Route path="/gift/create" element={<GiftEdit/>} />
        <Route path="/gift/:id" element={<GiftEdit/>} />

        <Route path="/bastion_facility" element={<BastionFacilityIndex/>} />
        <Route path="/bastion_facility/create" element={<BastionFacilityEdit/>} />
        <Route path="/bastion_facility/:id" element={<BastionFacilityEdit/>} />
        
        <Route path="/transform" element={<TransformIndex/>} />
        <Route path="/transform/create" element={<TransformEdit/>} />
        <Route path="/transform/:id" element={<TransformEdit/>} />

        <Route path="/background" element={<BackgroundIndex/>} />
        <Route path="/background/create" element={<BackgroundEdit/>} />
        <Route path="/background/:id" element={<BackgroundEdit/>} />
        
        <Route path="/resource" element={<ResourceIndex/>} />
        <Route path="/resource/create" element={<ResourceEdit/>} />
        <Route path="/resource/:id" element={<ResourceEdit/>} />

        <Route path="/item" element={<ItemIndex/>} />
        <Route path="/item/create" element={<ItemEdit/>} />
        <Route path="/item/:id" element={<ItemEdit/>} />

        <Route path="/rollable_table" element={<RollableTableIndex/>} />
        <Route path="/rollable_table/create" element={<RollableTableEdit/>} />
        <Route path="/rollable_table/:id" element={<RollableTableEdit/>} />

        <Route path="/loot_table" element={<LootTableIndex/>} />
        <Route path="/loot_table/create" element={<LootTableEdit/>} />
        <Route path="/loot_table/:id" element={<LootTableEdit/>} />

        <Route path="/creature" element={<CreatureIndex/>} />
        <Route path="/creature/create" element={<CreatureEdit/>} />
        <Route path="/creature/:id" element={<CreatureEdit/>} />
        
        <Route path="/creature_group" element={<CreatureGroupIndex/>} />
        <Route path="/creature_group/create" element={<CreatureGroupEdit/>} />
        <Route path="/creature_group/:id" element={<CreatureGroupEdit/>} />
        
        <Route path="/creature_gift" element={<CreatureGiftIndex/>} />
        <Route path="/creature_gift/create" element={<CreatureGiftEdit/>} />
        <Route path="/creature_gift/:id" element={<CreatureGiftEdit/>} />

        <Route path="/feat" element={<FeatIndex/>} />
        <Route path="/feat/create" element={<FeatEdit/>} />
        <Route path="/feat/:id" element={<FeatEdit/>} />

        <Route path="/eldritch_invocation" element={<EldritchInvocationIndex/>} />
        <Route path="/eldritch_invocation/create" element={<EldritchInvocationEdit/>} />
        <Route path="/eldritch_invocation/:id" element={<EldritchInvocationEdit/>} />

        <Route path="/fighting_style" element={<FightingStyleIndex/>} />
        <Route path="/fighting_style/create" element={<FightingStyleEdit/>} />
        <Route path="/fighting_style/:id" element={<FightingStyleEdit/>} />

        <Route path="/condition" element={<ConditionIndex/>} />
        <Route path="/condition/create" element={<ConditionEdit/>} />
        <Route path="/condition/:id" element={<ConditionEdit/>} />

        <Route path="/pact_boon" element={<PactBoonIndex/>} />
        <Route path="/pact_boon/create" element={<PactBoonEdit/>} />
        <Route path="/pact_boon/:id" element={<PactBoonEdit/>} />

        <Route path="/game_class" element={<GameClassIndex/>} />
        <Route path="/game_class/create" element={<GameClassEdit/>} />
        <Route path="/game_class/:id" element={<GameClassEdit/>} />

        <Route path="/subclass" element={<SubclassIndex/>} />
        <Route path="/subclass/create" element={<SubclassEdit/>} />
        <Route path="/subclass/:id" element={<SubclassEdit/>} />

        <Route path="/equipment_pack" element={<EquipmentPackIndex/>} />
        <Route path="/equipment_pack/create" element={<EquipmentPackEdit/>} />
        <Route path="/equipment_pack/:id" element={<EquipmentPackEdit/>} />

        <Route path="/magic_item_keyword" element={<MagicItemKeywordIndex/>} />
        <Route path="/magic_item_keyword/create" element={<MagicItemKeywordEdit/>} />
        <Route path="/magic_item_keyword/:id" element={<MagicItemKeywordEdit/>} />

        <Route path="/weapon_mastery_property" element={<WeaponMasteryPropertyIndex/>} />
        <Route path="/weapon_mastery_property/create" element={<WeaponMasteryPropertyEdit/>} />
        <Route path="/weapon_mastery_property/:id" element={<WeaponMasteryPropertyEdit/>} />

        <Route path="/race" element={<RaceIndex/>} />
        <Route path="/race/create" element={<RaceEdit/>} />
        <Route path="/race/:id" element={<RaceEdit/>} />

        <Route path="/subrace" element={<SubraceIndex/>} />
        <Route path="/subrace/create" element={<SubraceEdit/>} />
        <Route path="/subrace/:id" element={<SubraceEdit/>} />

        <Route path="/spell_list" element={<SpellListIndex/>} />
        <Route path="/spell_list/create" element={<SpellListEdit/>} />
        <Route path="/spell_list/:id" element={<SpellListEdit/>} />

        <Route path="/template_base" element={<TemplateBaseIndex/>} />
        <Route path="/template_base/create" element={<TemplateBaseEdit/>} />
        <Route path="/template_base/:id" element={<TemplateBaseEdit/>} />

        <Route path="/floating_feature" element={<FloatingFeatureIndex/>} />
        <Route path="/floating_feature/create" element={<FloatingFeatureEdit/>} />
        <Route path="/floating_feature/:id" element={<FloatingFeatureEdit/>} />

        <Route path="/creature_feature" element={<CreatureFeatureIndex/>} />
        <Route path="/creature_feature/create" element={<CreatureFeatureEdit/>} />
        <Route path="/creature_feature/:id" element={<CreatureFeatureEdit/>} />

        <Route path="/special_feature" element={<SpecialFeatureIndex/>} />
        <Route path="/special_feature/create" element={<SpecialFeatureEdit/>} />
        <Route path="/special_feature/:id" element={<SpecialFeatureEdit/>} />

        <Route path="/tool" element={<ToolIndex/>} />
        <Route path="/tool/create" element={<ToolEdit/>} />
        <Route path="/tool/:id" element={<ToolEdit/>} />

        <Route path="/standard_features" element={<StandardFeaturesIndex/>} />
        <Route path="/standard_features/create" element={<StandardFeaturesEdit/>} />
        <Route path="/standard_features/:id" element={<StandardFeaturesEdit/>} /> */}
      </Routes>
    );
  }
}
